import { toast } from "@aurumiq/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, qs, rawApi } from "@/lib/api";
import type {
  Paginated,
  RolesResponse,
  Staff,
  StaffCreateInput,
  StaffPatchInput,
} from "@/lib/types";

const BASE = "/api/v1/admin";

export const staffKeys = {
  list: (q: string) => ["admin", "staff", { q }] as const,
  all: ["admin", "staff"] as const,
  roles: ["admin", "roles"] as const,
};

export const STAFF_ROLES: Staff["role"][] = [
  "superadmin",
  "support",
  "sales",
  "content_editor",
  "pending",
];

export function useStaff(q = "") {
  return useQuery({
    queryKey: staffKeys.list(q),
    queryFn: () => rawApi.get<Paginated<Staff>>(`${BASE}/staff${qs({ q })}`),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: staffKeys.roles,
    staleTime: 5 * 60_000,
    queryFn: () => rawApi.get<RolesResponse>(`${BASE}/roles`),
  });
}

function useStaffMutation<TVars, TData = unknown>(
  fn: (vars: TVars) => Promise<TData>,
  success: string | ((data: TData) => string),
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: staffKeys.all });
      toast.success(typeof success === "function" ? success(data) : success);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useCreateStaff() {
  return useStaffMutation(
    (input: StaffCreateInput) => rawApi.post<Staff>(`${BASE}/staff`, input),
    (s) => `${s.email} added`,
  );
}

export function usePatchStaff() {
  return useStaffMutation(
    ({ id, ...input }: StaffPatchInput & { id: string }) =>
      rawApi.patch<Staff>(`${BASE}/staff/${id}`, input),
    "Staff member updated",
  );
}

export function useSetStaffPassword() {
  return useStaffMutation(
    ({ id, new_password }: { id: string; new_password: string }) =>
      rawApi.put<void>(`${BASE}/staff/${id}/password`, { new_password }),
    "Password set",
  );
}

export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) =>
      rawApi.post<void>("/api/v1/auth/password", input),
    onSuccess: () => toast.success("Password changed"),
    onError: (err) => toast.error(errorMessage(err)),
  });
}
